import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { switchRoleIfNeeded } from '../utils/roleSwitch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SOCKET_PATH = '/api/socket.io';

/**
 * Per-`notification_type` toast renderer config. Each entry describes
 * how a `realtime_notification` socket event from the backend is shown
 * as a sonner toast. Keep this list in sync with
 * `UserNotificationService.REALTIME_TOAST_TYPES` on the backend — if a
 * type isn't listed here, the toast falls back to the generic shape.
 */
const TYPE_RENDERERS = {
  event_reminder_2h: {
    defaultActionLabel: 'View',
    defaultUrl: '/guest/profile',
    duration: 8000,
  },
  venue_review_received: {
    defaultActionLabel: 'Read',
    defaultUrl: '/venue/settings/reviews',
    duration: 10000,
  },
  ical_reservation: {
    defaultActionLabel: 'Open',
    defaultUrl: '/host/reservations',
    duration: 10000,
  },
  host_payout_sent: {
    defaultActionLabel: 'See payout',
    defaultUrl: '/host/settings/payouts/history',
    duration: 10000,
  },
  host_pool_qualified: {
    defaultActionLabel: 'See earnings',
    defaultUrl: '/host/earnings',
    duration: 10000,
  },
  venue_went_live: {
    defaultActionLabel: 'Open venue',
    defaultUrl: '/venue',
    duration: 12000,
  },
  venue_auto_live: {
    defaultActionLabel: 'Open admin',
    defaultUrl: '/admin',
    duration: 12000,
  },
  admin_venue_application_submitted: {
    defaultActionLabel: 'Review',
    defaultUrl: '/admin',
    duration: 12000,
  },
  admin_host_application_submitted: {
    defaultActionLabel: 'Review',
    defaultUrl: '/admin',
    duration: 12000,
  },
  admin_venue_stale_hold: {
    defaultActionLabel: 'Open admin',
    defaultUrl: '/admin',
    duration: 12000,
  },
  support_admin_reply: {
    defaultActionLabel: 'Open chat',
    defaultUrl: '/support/contact',
    duration: 10000,
  },
  support_admin_message: {
    defaultActionLabel: 'Open chat',
    defaultUrl: '/guest/messages',
    duration: 10000,
  },
  support_user_declined: {
    defaultActionLabel: 'Open chat',
    defaultUrl: '/admin',
    duration: 10000,
  },
  support_user_message: {
    defaultActionLabel: 'Open inbox',
    defaultUrl: '/admin',
    duration: 10000,
  },
  // Regular chat messages (host↔guest, guest↔venue, guest↔admin-venue, etc.).
  // The backend always sets `payload.url` to the specific conversation
  // path, so the renderer's `defaultUrl` is only a fallback.
  new_message: {
    defaultActionLabel: 'Open chat',
    defaultUrl: '/messages',
    duration: 8000,
  },
};

const GENERIC_RENDERER = {
  defaultActionLabel: 'Open',
  defaultUrl: '/',
  duration: 8000,
};

/**
 * RealtimeProvider — opens a single Socket.IO connection for every
 * authenticated user (regardless of role) and listens for
 * `realtime_notification` events. Each event pops a sonner toast.
 *
 * Independent from `CallContext` (which only opens its own socket for
 * voice-call-allowed roles). Keeping them separate means a venue owner
 * or admin-staff — who never receives calls — still gets the realtime
 * toast channel.
 */
export function RealtimeProvider({ user, children }) {
  const navigate = useNavigate();
  const socketRef = useRef(null);

  useEffect(() => {
    if (!user || !user.id) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      return undefined;
    }

    const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : null;
    if (!token) {
      return undefined;
    }

    const socket = io(BACKEND_URL, {
      path: SOCKET_PATH,
      transports: ['websocket', 'polling'],
      auth: { token },
    });
    socketRef.current = socket;

    socket.on('realtime_notification', (payload) => {
      if (!payload || typeof payload !== 'object') return;
      const type = payload.type || 'general';
      const renderer = TYPE_RENDERERS[type] || GENERIC_RENDERER;
      const title = payload.title || 'New update';
      const body = payload.body || '';
      const url = payload.url || renderer.defaultUrl;
      const stableId = payload.notification_id
        ? `realtime-${payload.notification_id}`
        : `realtime-${type}-${Date.now()}`;
      // Chat messages carry the recipient's expected role in `metadata.target_role`.
      // Tapping the toast should ALWAYS land the user inside the correct chat,
      // switching their active role on the fly if it doesn't match.
      const targetRole = payload?.metadata?.target_role || '';

      toast(title, {
        description: body,
        duration: renderer.duration,
        action: {
          label: renderer.defaultActionLabel,
          onClick: async () => {
            if (targetRole) {
              await switchRoleIfNeeded(targetRole);
              // Hard navigation so the role-specific provider tree mounts.
              if (typeof window !== 'undefined') {
                window.location.href = url;
                return;
              }
            }
            navigate(url);
          },
        },
        id: stableId,
      });
    });

    // Live announcement banner refresh — fired by admin announcement
    // create/update/delete on the backend.
    socket.on('announcement_changed', () => {
      try {
        window.dispatchEvent(new CustomEvent('allure:announcement-changed'));
      } catch {
        /* noop */
      }
    });

    // Live venue rating refresh — fired when any guest submits a new
    // review. The guest map listens for this and patches the venue
    // badge in place without a full refetch.
    socket.on('venue_review_changed', (payload) => {
      try {
        window.dispatchEvent(new CustomEvent('allure:venue-review-changed', { detail: payload || {} }));
      } catch {
        /* noop */
      }
    });

    // Live Allure-Support thread refresh — fired on the backend whenever a
    // new support message lands (admin → user or user → admin). The user-
    // side `/support/contact` page and the admin panel both listen for
    // this window event to refetch their open thread without requiring
    // a page navigation.
    socket.on('support_thread_changed', (payload) => {
      try {
        window.dispatchEvent(new CustomEvent('allure:support-thread-changed', { detail: payload || {} }));
      } catch {
        /* noop */
      }
    });

    // Live refresh for regular messaging conversations (host↔guest,
    // guest↔venue, guest↔admin-venue). The backend emits this alongside
    // the toast + push so any open ChatPage / messages list can patch
    // itself without the user navigating away.
    socket.on('conversation_message_received', (payload) => {
      try {
        window.dispatchEvent(new CustomEvent('allure:conversation-message-received', { detail: payload || {} }));
      } catch {
        /* noop */
      }
      // Ack delivery back to the server so the SENDER's bubbles flip to
      // the grey double tick — even when this recipient's chat isn't
      // currently open. Best-effort; failures are swallowed.
      try {
        const token = window.localStorage.getItem('token');
        const convoId = payload?.conversation_id;
        if (token && convoId) {
          axios.post(
            `${API}/messages/mark-delivered`,
            { conversation_id: convoId },
            { headers: { Authorization: `Bearer ${token}` } },
          ).catch(() => { /* noop */ });
        }
      } catch { /* noop */ }
    });

    // WhatsApp-style read/delivered receipts. Emitted to the SENDER's
    // personal room whenever the recipient acks messages (delivered) or
    // opens the chat (read). The chat page listens and flips ticks in
    // place — no polling.
    socket.on('conversation_messages_delivered', (payload) => {
      try {
        window.dispatchEvent(new CustomEvent('allure:conversation-messages-delivered', { detail: payload || {} }));
      } catch {
        /* noop */
      }
    });
    socket.on('conversation_messages_read', (payload) => {
      try {
        window.dispatchEvent(new CustomEvent('allure:conversation-messages-read', { detail: payload || {} }));
      } catch {
        /* noop */
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user, navigate]);

  return children;
}
